import { createFileRoute, Link, useRouter } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { FaArrowLeftLong } from 'react-icons/fa6'
import { queryClient } from '@/lib/utils/queryUtils'
import { Check, GitCompareArrows } from 'lucide-react'
import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { errorToast } from '@/lib/utils/ToastUtils'
import { toast } from 'sonner'
import { ClientCheckinResponseLoadingState } from '@/components/client/ClientTabLoadingStates'
import { mediumDateTimeString } from '@/lib/utils/DateUtils'
import MetricChartDialog from '@/components/metricsComponents/MetricChartDialog'
import FormResponseView, {
  QuestionResponse,
} from '@/components/forms/FormResponseView'
import { invalidateCoachOnboardingQueries } from '@/lib/utils/onboardingCache'
import { AiCheckinSummary } from '@/components/checkin/AiCheckinSummary'
import { useOrgFeatureFlags } from '@/lib/queries/useOrgFeatureFlags'
import { useDocumentTitle } from '@/lib/hooks/useDocumentTitle'
import { trpc } from '@/lib/trpc/client'
import { trpcUtils } from '@/lib/trpc/vanilla'
import { patchKeyedListCache, patchQueryCache } from '@trainrr/api-client'

export const Route = createFileRoute(
  '/_auth/_dashboard/clients/$clientId/_layout/checkin/$formId/$responseId',
)({
  component: ClientCheckinResponse,
  pendingComponent: ClientCheckinResponseLoadingState,
  loader: ({ params }) =>
    trpcUtils.checkins.getResponse.ensureData({
      clientId: params.clientId,
      formId: params.formId,
      responseId: params.responseId,
    }),
})

type CheckinResponse = Awaited<
  ReturnType<typeof trpcUtils.checkins.getResponse.ensureData>
>

function ClientCheckinResponse() {
  const { responseId, formId, clientId } = Route.useParams()
  const { data: form } = trpc.checkins.getResponse.useQuery({
    clientId,
    formId,
    responseId,
  })
  if (!form) return <ClientCheckinResponseLoadingState />
  return <ClientCheckinResponseView form={form} />
}

function ClientCheckinResponseView({ form }: { form: CheckinResponse }) {
  const { responseId, formId, clientId } = Route.useParams()
  const router = useRouter()
  const [reviewCheckInDialogOpened, setReviewCheckInDialogOpened] =
    useState(false)
  const [metricDialogId, setMetricDialogId] = useState<string>()
  const isOnboarding = form.form?.type === 'onboarding'
  const { aiCheckinSummary } = useOrgFeatureFlags()
  const coachReviewNotes = form.coach_res?.[0]?.notes?.trim() ?? ''

  const reviewCheckIn = trpc.checkins.review.useMutation()
  const completeOnboardingItem = trpc.onboarding.completeItem.useMutation()
  const utils = trpc.useUtils()

  useDocumentTitle(form.form?.name || 'Check-in', { priority: 2 })

  const handleGoback = () => {
    window.history.back()
  }

  const handleCheckInReview = async (notes: string) => {
    try {
      await reviewCheckIn.mutateAsync({
        responseId,
        clientId,
        formId,
        notes,
      })
    } catch (error) {
      console.error('Error reviewing check in', error)
      errorToast(
        error instanceof Error ? error : null,
        'Error reviewing check in',
      )
      throw error
    }

    patchQueryCache(
      utils.checkins.getResponse,
      { clientId, formId, responseId },
      (old) => ({
        ...old,
        reviewed: true,
        coach_res: [{ notes }],
      }),
    )
    patchQueryCache(utils.checkins.inbox, undefined, (old) => ({
      ...old,
      responses: old.responses.map((response) =>
        response.id === responseId
          ? {
              ...response,
              reviewed: true,
              coach_res: response.coach_res.length
                ? response.coach_res
                : [{ created_at: new Date().toISOString() }],
            }
          : response,
      ),
    }))
    patchKeyedListCache(utils.checkins.listForClient, { clientId }, (old) =>
      old.map((row) =>
        row.id === responseId ? { ...row, reviewed: true } : row,
      ),
    )

    try {
      await completeOnboardingItem.mutateAsync({
        key: 'coach_onboarding',
        version: 1,
        itemKey: 'setup.checkins.manage',
        source: 'auto',
        metadata: {
          source: 'checkin-review',
          response_id: responseId,
          form_id: formId,
        },
      })
      await invalidateCoachOnboardingQueries(queryClient)
    } catch (onboardingError) {
      console.warn(
        'Failed to complete onboarding item setup.checkins.manage',
        onboardingError,
      )
    }

    await utils.checkins.waitingCount.invalidate()
    await router.invalidate()
    toast.success('Check in reviewed successfully')
  }

  return (
    <>
      <ReviewCheckInDialog
        isOpen={reviewCheckInDialogOpened}
        onOpenChange={setReviewCheckInDialogOpened}
        onSave={handleCheckInReview}
        preNotes={form.coach_res?.[0]?.notes ?? ''}
        isSaving={reviewCheckIn.isPending}
      />
      <MetricChartDialog
        open={metricDialogId !== undefined}
        onOpenChange={(open) => {
          if (!open) {
            setMetricDialogId(undefined)
          }
        }}
        metricId={metricDialogId}
        clientId={clientId}
      />

      <div className="ph-no-capture w-full md:w-[90%] mx-auto">
        <Card className="min-w-[30%] h-full mb-10 p-4">
          <div className="mb-10 flex flex-row justify-between">
            <div className="flex gap-4 items-center">
              <Button size={'icon'} variant={'ghost'} onClick={handleGoback}>
                <FaArrowLeftLong />
              </Button>
              <span className="flex flex-col gap-0.5">
                <h1 className="text-lg font-medium">{form.form?.name} </h1>
                <h1 className="text-sm text-muted-foreground">
                  {form.response_date &&
                    mediumDateTimeString(form.response_date)}
                </h1>
              </span>
            </div>
            {!isOnboarding ? (
              <div className="flex flex-row gap-4 items-center">
                <Button
                  size={'sm'}
                  onClick={() => setReviewCheckInDialogOpened(true)}
                >
                  {form.reviewed && <Check className="w-4 h-4 mr-2" />}
                  {form.reviewed ? 'Reviewed' : 'Review check-in'}
                </Button>

                <Link
                  className="hidden sm:block"
                  to="/clients/$clientId/checkin/compare"
                  params={{ clientId }}
                  search={{ date: form?.response_date }}
                >
                  <Button size={'sm'} variant={'outline'}>
                    <GitCompareArrows className="w-4 h-4 mr-2" />
                    Compare
                  </Button>
                </Link>
              </div>
            ) : null}
          </div>

          {coachReviewNotes ? (
            <div className="mb-6 rounded-lg border bg-muted/40 p-4">
              <p className="mb-2 text-sm font-medium">Coach review notes</p>
              <p className="whitespace-pre-wrap text-sm text-muted-foreground">
                {coachReviewNotes}
              </p>
            </div>
          ) : null}

          {aiCheckinSummary && !isOnboarding && (
            <AiCheckinSummary responseId={responseId} clientId={clientId} />
          )}

          <div className="flex gap-4 flex-col">
            {form?.responses.map((response) => (
              <div key={response.q_num} className="flex gap-3 flex-col">
                <FormResponseView
                  response={response as QuestionResponse}
                  clientId={clientId}
                  variant={isOnboarding ? 'lead' : 'default'}
                  metricResponseSelected={(metricId) =>
                    setMetricDialogId(metricId)
                  }
                />
              </div>
            ))}
          </div>
        </Card>
      </div>
    </>
  )
}

interface ReviewCheckInDialogProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  onSave: (notes: string) => Promise<void>
  preNotes: string
  isSaving: boolean
}

function ReviewCheckInDialog({
  isOpen,
  onOpenChange,
  onSave,
  preNotes,
  isSaving,
}: ReviewCheckInDialogProps) {
  const [notes, setNotes] = useState(preNotes)

  const handleSave = async () => {
    try {
      await onSave(notes)
      onOpenChange(false)
    } catch {
      // Stay open so the coach can retry after the error toast.
    }
  }

  useEffect(() => {
    if (isOpen) {
      setNotes(preNotes)
    }
  }, [isOpen, preNotes])

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!isSaving) {
          onOpenChange(open)
        }
      }}
      analyticsName="checkin_review_dialog"
    >
      <DialogContent className="ph-no-capture">
        <DialogHeader>
          <DialogTitle>Check-In Review</DialogTitle>
        </DialogHeader>
        <DialogDescription>
          The client will be notified if any comments are added
        </DialogDescription>
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Enter your notes here..."
          disabled={isSaving}
        />
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Reviewing...' : 'Review'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default ReviewCheckInDialog
